
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { frankLogger } from "@/services/frank/logger";

export const fetchAssociationId = async (): Promise<string | null> => {
  const correlationId = frankLogger.generateCorrelationId();
  console.log(`[${correlationId}] Starting association ID fetch...`);
  
  try {
    // First try sessionStorage, then fallback to localStorage
    let memberId = sessionStorage.getItem('organization_member_id');
    
    if (!memberId) {
      memberId = localStorage.getItem('manager_member_id');
      if (memberId) {
        console.log(`[${correlationId}] Using member ID from localStorage backup:`, memberId);
        // Restore to sessionStorage for future use
        sessionStorage.setItem('organization_member_id', memberId);
      }
    }
    
    if (!memberId) {
      const errorMsg = "No member ID found in session or local storage";
      console.error(`[${correlationId}] ${errorMsg}`);
      
      await frankLogger.logError({
        correlationId,
        errorType: 'INVALID_CONFIG',
        errorMessage: errorMsg,
        errorDetails: { 
          sessionStorage: sessionStorage.getItem('organization_member_id'),
          localStorage: localStorage.getItem('manager_member_id')
        }
      });
      
      return null;
    }
    
    console.log(`[${correlationId}] Found member ID:`, memberId);
    
    const { data: memberData, error: memberError } = await supabase
      .from('organization_members')
      .select('organization_id, full_name, email, role')
      .eq('id', memberId)
      .single();
    
    if (memberError) {
      const errorMsg = "Error fetching member data";
      console.error(`[${correlationId}] ${errorMsg}:`, memberError);
      
      await frankLogger.logError({
        correlationId,
        memberId,
        errorType: 'INVALID_CONFIG',
        errorMessage: errorMsg,
        errorDetails: { supabaseError: memberError, memberId }
      });
      
      toast.error("Could not retrieve your association information");
      return null;
    }
    
    if (!memberData?.organization_id) {
      const errorMsg = "No organization ID found for member";
      console.error(`[${correlationId}] ${errorMsg}`);
      
      await frankLogger.logError({
        correlationId,
        memberId,
        errorType: 'INVALID_CONFIG',
        errorMessage: errorMsg,
        errorDetails: { memberData }
      });
      
      return null;
    }
    
    console.log(`[${correlationId}] Member verified:`, {
      name: memberData.full_name,
      email: memberData.email,
      role: memberData.role,
      organizationId: memberData.organization_id
    });
    
    // Now fetch the organization to get the association_id (numeric)
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select('association_id, name')
      .eq('id', memberData.organization_id)
      .single();
    
    if (orgError) {
      const errorMsg = "Error fetching organization data";
      console.error(`[${correlationId}] ${errorMsg}:`, orgError);
      
      await frankLogger.logError({
        correlationId,
        memberId,
        organizationId: memberData.organization_id,
        errorType: 'INVALID_CONFIG',
        errorMessage: errorMsg,
        errorDetails: { supabaseError: orgError, organizationId: memberData.organization_id }
      });
      
      toast.error("Could not retrieve association information");
      return null;
    }
    
    if (!orgData?.association_id) {
      const errorMsg = "No association ID found for organization";
      console.error(`[${correlationId}] ${errorMsg}`);
      
      await frankLogger.logError({
        correlationId,
        memberId,
        organizationId: memberData.organization_id,
        errorType: 'INVALID_CONFIG',
        errorMessage: errorMsg,
        errorDetails: { orgData }
      });
      
      return null;
    }
    
    // Validate that we have a valid numeric association_id
    const associationId = orgData.association_id;
    if (typeof associationId !== 'number' || associationId <= 0) {
      const errorMsg = `Invalid association ID: ${associationId}`;
      console.error(`[${correlationId}] ${errorMsg}`);
      
      await frankLogger.logError({
        correlationId,
        memberId,
        organizationId: memberData.organization_id,
        associationId: associationId as any,
        errorType: 'INVALID_CONFIG',
        errorMessage: errorMsg,
        errorDetails: { associationId, type: typeof associationId }
      });
      
      return null;
    }
    
    // Final validation: ensure member context is complete
    console.log(`[${correlationId}] Association context validated successfully:`, {
      memberId,
      memberName: memberData.full_name,
      organizationId: memberData.organization_id,
      organizationName: orgData.name,
      associationId
    });
    
    return associationId.toString();
  } catch (error) {
    const errorMsg = "Failed to fetch association ID";
    console.error(`[${correlationId}] ${errorMsg}:`, error);
    
    await frankLogger.logError({
      correlationId,
      errorType: 'UNKNOWN_ERROR',
      errorMessage: errorMsg,
      errorDetails: { 
        error: error instanceof Error ? error.message : error,
        stack: error instanceof Error ? error.stack : undefined
      }
    });
    
    toast.error("Could not connect to the database");
    return null;
  }
};
